import {useState} from "react";
import {CheckCircle2, CircleDot, ExternalLink, FileText, Image, Link as LinkIcon, UserCog} from "lucide-react";
import {getApiBaseUrl} from "../api/client";
import {formatMoney, formatNumber} from "../utils/format";
import {DataState} from "./DataState";
import {StatusBadge} from "./StatusBadge";

const dayLabels = [
    ["monday", "Пн"],
    ["tuesday", "Вт"],
    ["wednesday", "Ср"],
    ["thursday", "Чт"],
    ["friday", "Пт"],
    ["saturday", "Сб"],
    ["sunday", "Вс"],
];

const endpointLabels = {
    migration: "Migration readiness",
    jobs: "Celery jobs",
    ledger: "Ledger health",
    providers: "Колбэки провайдеров",
};

const endpointContracts = {
    migration: "/api/v2/admin/system/migration-readiness",
    jobs: "/api/v2/admin/system/jobs",
    ledger: "/api/v2/admin/dashboard/ledger-health",
    providers: "/api/v2/admin/dashboard/provider-health",
};

const asArray = (value) => (Array.isArray(value) ? value : []);

export const displayValue = (value, fallback = "—") => {
    if (value === null || value === undefined || value === "") {
        return fallback;
    }
    return value;
};

export const yesNo = (value) => (value ? "Да" : "Нет");

export const mediaUrl = (value) => {
    if (!value) {
        return "";
    }
    if (/^https?:\/\//i.test(value)) {
        return value;
    }
    return `${getApiBaseUrl()}${String(value).startsWith("/") ? value : `/${value}`}`;
};

export const ScheduleGrid = ({entity}) => (
    <div className="admin-schedule-grid">
        {dayLabels.map(([key, label]) => {
            const start = entity?.[`${key}_start`];
            const end = entity?.[`${key}_end`];
            const closed = !start && !end;
            return (
                <div className={closed ? "is-muted" : ""} key={key}>
                    <span>{label}</span>
                    <strong>{closed ? "Закрыто" : `${start || "—"} - ${end || "—"}`}</strong>
                </div>
            );
        })}
    </div>
);

export const StaffCards = ({staff = []}) => {
    const rows = asArray(staff);
    if (!rows.length) {
        return <DataState title="Сотрудники не указаны" copy="Для филиала не передан список персонала." />;
    }

    return (
        <div className="admin-mini-grid">
            {rows.map((person) => (
                <article className="admin-mini-card" key={person.id || person.login || person.phone_number}>
                    <div className="admin-mini-card-icon">
                        <UserCog size={16} />
                    </div>
                    <div>
                        <strong>{displayValue(person.name, "Без имени")}</strong>
                        <span>{displayValue(person.position, "Роль не указана")}</span>
                        <span>{displayValue(person.phone_number)}</span>
                        <small>Мотивация: {displayValue(person.custom_reward_percent, 0)}%</small>
                    </div>
                </article>
            ))}
        </div>
    );
};

export const MediaLinks = ({items = []}) => {
    const rows = items.filter((item) => item?.url);
    if (!rows.length) {
        return <DataState title="Медиа не загружены" copy="Для этой сущности backend не вернул публичные файлы." />;
    }

    return (
        <div className="admin-media-list">
            {rows.map((item) => {
                const Icon = item.type === "document" ? FileText : Image;
                const href = mediaUrl(item.url);
                return (
                    <a href={href} key={`${item.label}-${item.url}`} rel="noreferrer" target="_blank">
                        <Icon size={16} />
                        <span>{item.label}</span>
                        <ExternalLink size={14} />
                    </a>
                );
            })}
        </div>
    );
};

export const ExternalLinks = ({links = []}) => {
    const rows = links.filter((item) => item?.url);
    if (!rows.length) {
        return <DataState title="Ссылки не указаны" copy="Instagram, WhatsApp или 2GIS не переданы для этого филиала." />;
    }

    return (
        <div className="admin-media-list">
            {rows.map((item) => (
                <a href={item.url} key={`${item.label}-${item.url}`} rel="noreferrer" target="_blank">
                    <LinkIcon size={16} />
                    <span>{item.label}</span>
                    <ExternalLink size={14} />
                </a>
            ))}
        </div>
    );
};

export const GalleryGrid = ({photos = []}) => {
    const rows = asArray(photos)
        .map((item) => item.photo || item.image || item.file || item.url)
        .filter(Boolean);

    if (!rows.length) {
        return <DataState title="Галерея пустая" copy="Backend не вернул фотографии филиала." />;
    }

    return (
        <div className="admin-gallery-grid">
            {rows.map((url) => (
                <a href={mediaUrl(url)} key={url} rel="noreferrer" target="_blank">
                    <img alt="" src={mediaUrl(url)} />
                </a>
            ))}
        </div>
    );
};

export const EndpointReadinessGrid = ({data = {}}) => (
    <div className="admin-readiness-grid">
        {Object.keys(endpointLabels).map((key) => {
            const item = data[key] || {};
            const status = item.ok ? "connected" : `missing${item.error?.response?.status ? ` ${item.error.response.status}` : ""}`;
            return (
                <article className="admin-readiness-card" key={key}>
                    <div>
                        <p>{endpointLabels[key]}</p>
                        <strong>{endpointContracts[key]}</strong>
                    </div>
                    <StatusBadge tone={item.ok ? "success" : "warning"}>{status}</StatusBadge>
                </article>
            );
        })}
    </div>
);

export const OperationsFlow = ({title, copy, steps = [], facts = []}) => (
    <article className="admin-card admin-ops-panel">
        <div className="admin-ops-panel-head">
            <span className="admin-ops-mark">
                <CircleDot size={16} />
            </span>
            <div>
                <p className="admin-card-title">{title}</p>
                {copy && <p className="admin-card-note">{copy}</p>}
            </div>
        </div>

        {steps.length > 0 && (
            <ol className="admin-ops-steps">
                {steps.map((step, index) => (
                    <li key={step.title || index}>
                        <span>{index + 1}</span>
                        <div>
                            <strong>{step.title}</strong>
                            <small>{step.copy}</small>
                        </div>
                    </li>
                ))}
            </ol>
        )}

        {facts.length > 0 && (
            <div className="admin-ops-facts">
                {facts.map((fact) => (
                    <div key={fact.label}>
                        <span>{fact.label}</span>
                        <strong>{fact.value}</strong>
                    </div>
                ))}
            </div>
        )}
    </article>
);

export const ActionChecklist = ({items = []}) => (
    <ul className="admin-action-checklist">
        {items.map((item) => (
            <li key={item}>
                <CheckCircle2 size={15} />
                <span>{item}</span>
            </li>
        ))}
    </ul>
);

const getReferralDetails = (node = {}) => {
    const details = node.user_details || node;
    const children = asArray(node.invited_users);
    const name = details.name || details.phone_number || "Без имени";
    const phone = details.phone_number || "";
    const count = Number(node.total_invited ?? children.length ?? 0);
    const initial = String(name || phone || "?").trim().charAt(0).toUpperCase();

    return {
        children,
        count,
        id: details.user_id || details.id || phone || name,
        initial: initial || "?",
        name,
        phone,
    };
};

export const ReferralTree = ({node, level = 0}) => {
    const children = asArray(node?.invited_users);
    const details = node?.user_details || {};
    const name = details.name || details.phone_number || `Узел ${level + 1}`;

    if (!node || (!details.name && !details.phone_number && !children.length)) {
        return <DataState title="Сеть пустая" copy="Backend не вернул данные по выбранной реферальной сети." />;
    }

    return (
        <div className="admin-tree-node">
            <div className="admin-tree-card">
                <span>Уровень {level}</span>
                <strong>{name}</strong>
                <small>
                    {displayValue(details.phone_number)} · приглашено {formatNumber(node.total_invited || children.length)}
                </small>
            </div>
            {children.length > 0 && (
                <div className="admin-tree-children">
                    {children.map((child, index) => (
                        <ReferralTree
                            key={child?.user_details?.user_id || child?.user_details?.phone_number || `${level}-${index}`}
                            level={level + 1}
                            node={child}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export const ReferralGraphCanvas = ({node, maxDepth = 7}) => {
    const [selectedPath, setSelectedPath] = useState([]);
    const rootDetails = getReferralDetails(node);

    if (!node || (!rootDetails.name && !rootDetails.phone && !rootDetails.children.length)) {
        return <DataState title="Сеть пустая" copy="Backend не вернул данные по выбранной реферальной сети." />;
    }

    const columns = [];
    let parent = node;

    for (let level = 1; level <= maxDepth; level += 1) {
        const parentDetails = getReferralDetails(parent);
        if (!parentDetails.children.length) {
            break;
        }

        const selectedIndex = selectedPath[level - 1] ?? null;
        columns.push({
            level,
            nodes: parentDetails.children,
            selectedIndex,
        });

        if (selectedIndex === null || !parentDetails.children[selectedIndex]) {
            break;
        }

        parent = parentDetails.children[selectedIndex];
    }

    const rootX = 34;
    const rootY = 104;
    const rootWidth = 300;
    const nodeWidth = 275;
    const nodeHeight = 68;
    const columnGap = 112;
    const columnX = (index) => rootX + rootWidth + columnGap + index * (nodeWidth + columnGap);
    const nodeY = (index) => 76 + index * 82;
    const maxRows = Math.max(1, ...columns.map((column) => column.nodes.length));
    const canvasWidth = Math.max(860, columnX(Math.max(columns.length - 1, 0)) + nodeWidth + 56);
    const canvasHeight = Math.max(520, nodeY(maxRows - 1) + nodeHeight + 96);

    const selectNode = (level, index) => {
        setSelectedPath((current) => {
            const next = current.slice(0, level - 1);
            next[level - 1] = current[level - 1] === index ? null : index;
            return next.filter((value) => value !== null);
        });
    };

    const nodePosition = (columnIndex, nodeIndex) => ({
        x: columnX(columnIndex),
        y: nodeY(nodeIndex),
    });

    const parentPosition = (columnIndex) => {
        if (columnIndex === 0) {
            return {x: rootX, y: rootY, width: rootWidth, height: nodeHeight};
        }

        const previous = columns[columnIndex - 1];
        const selectedIndex = previous?.selectedIndex ?? 0;
        const position = nodePosition(columnIndex - 1, selectedIndex);
        return {...position, width: nodeWidth, height: nodeHeight};
    };

    return (
        <div className="admin-ref-graph-shell">
            <div className="admin-ref-graph-title">
                <h2>{rootDetails.name}</h2>
                <p>{displayValue(rootDetails.phone)} · КОРЕНЬ</p>
            </div>
            <div className="admin-ref-graph-scroll">
                <div className="admin-ref-graph-canvas" style={{height: canvasHeight, width: canvasWidth}}>
                    <svg className="admin-ref-graph-lines" height={canvasHeight} width={canvasWidth}>
                        {columns.flatMap((column, columnIndex) => {
                            const parentBox = parentPosition(columnIndex);
                            const startX = parentBox.x + parentBox.width;
                            const startY = parentBox.y + parentBox.height / 2;

                            return column.nodes.map((child, childIndex) => {
                                const childBox = nodePosition(columnIndex, childIndex);
                                const endX = childBox.x;
                                const endY = childBox.y + nodeHeight / 2;
                                const isSelected = column.selectedIndex === childIndex;
                                return (
                                    <path
                                d={`M ${startX} ${startY} C ${startX + 70} ${startY}, ${endX - 78} ${endY}, ${endX} ${endY}`}
                                        key={`${column.level}-${childIndex}`}
                                        className={isSelected ? "is-selected" : ""}
                                    />
                                );
                            });
                        })}
                    </svg>

                    <button
                        className="admin-ref-node admin-ref-node-root"
                        style={{height: nodeHeight, left: rootX, top: rootY, width: rootWidth}}
                        type="button"
                    >
                        <span className="admin-ref-avatar">{rootDetails.initial}</span>
                        <span className="admin-ref-node-copy">
                            <strong>{rootDetails.name}</strong>
                            <small>{displayValue(rootDetails.phone)} · КОРЕНЬ</small>
                        </span>
                        <span className="admin-ref-count">{formatNumber(rootDetails.count)}</span>
                    </button>

                    {columns.map((column, columnIndex) =>
                        column.nodes.map((child, childIndex) => {
                            const details = getReferralDetails(child);
                            const position = nodePosition(columnIndex, childIndex);
                            const isSelected = column.selectedIndex === childIndex;
                            const hasChildren = details.children.length > 0;

                            return (
                                <button
                                    className={`admin-ref-node ${isSelected ? "is-selected" : ""}`}
                                    key={`${column.level}-${details.id}-${childIndex}`}
                                    onClick={() => selectNode(column.level, childIndex)}
                                    style={{height: nodeHeight, left: position.x, top: position.y, width: nodeWidth}}
                                    type="button"
                                >
                                    <span className="admin-ref-avatar">{details.initial}</span>
                                    <span className="admin-ref-node-copy">
                                        <em>Уровень {column.level}</em>
                                        <strong>{details.name}</strong>
                                        <small>{displayValue(details.phone)}</small>
                                    </span>
                                    <span className={`admin-ref-count ${hasChildren ? "has-children" : ""}`}>
                                        {formatNumber(details.count)}
                                    </span>
                                </button>
                            );
                        }),
                    )}
                </div>
            </div>
            <p className="admin-ref-graph-hint">Клик по карточке раскрывает следующий уровень. Максимум {maxDepth} поколений.</p>
        </div>
    );
};

export const WalletSignalGrid = ({user = {}, account = {}}) => (
    <div className="admin-signal-grid">
        <div>
            <span>Ref-code</span>
            <strong>{displayValue(user.ref_code)}</strong>
        </div>
        <div>
            <span>Доля в Клубе</span>
            <strong>{displayValue(user.club_part)}</strong>
        </div>
        <div>
            <span>Приглашено</span>
            <strong>{formatNumber(user.invited_count)}</strong>
        </div>
        <div>
            <span>Spent / filled</span>
            <strong>{formatMoney(user.spent_sum)} / {formatMoney(user.fill_sum)}</strong>
        </div>
        <div>
            <span>Покрытие аккаунта</span>
            <strong>{Object.keys(account || {}).length ? "connected" : "limited"}</strong>
        </div>
    </div>
);
